import React, { useMemo, useState } from "react";
import { Box, Paper, Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";

const FinOpsMonthlyCostChart = ({ data }) => {
  const [chartType, setChartType] = useState("bar"); // "bar" or "line"

  const chartData = useMemo(() => {
    if (!data || !data.finOpsDashboardMonthlyDTO) {
      return {
        categories: [],
        series: [],
        accountNames: [],
      };
    }

    const monthlyData = data.finOpsDashboardMonthlyDTO;
    
    // Month mapping with full names
    const monthMap = {
      jan: "January",
      feb: "February",
      march: "March",
      april: "April",
      may: "May",
      june: "June",
      july: "July",
      aug: "August",
      sept: "September",
      oct: "October",
      nov: "November",
      dec: "December",
    };

    // Get current year
    const currentYear = new Date().getFullYear();

    // Month order (all 12 months)
    const monthOrder = ["jan", "feb", "march", "april", "may", "june", "july", "aug", "sept", "oct", "nov", "dec"];
    
    // Collect all unique account numbers and their total costs across all months
    const accountTotals = {};
    
    monthOrder.forEach((monthKey) => {
      const monthData = monthlyData[monthKey];
      const accountsMap = monthData?.accountsCostMonthMap;
      
      if (accountsMap && typeof accountsMap === 'object') {
        Object.keys(accountsMap).forEach((accountNumber) => {
          if (!accountTotals[accountNumber]) {
            accountTotals[accountNumber] = 0;
          }
          accountTotals[accountNumber] += accountsMap[accountNumber] || 0;
        });
      }
    });

    // Sort accounts by total cost
    const sortedAccountEntries = Object.entries(accountTotals)
      .sort((a, b) => b[1] - a[1]);
    
    // Limit to top 10 accounts for readability (too many segments/lines become unreadable)
    const MAX_ACCOUNTS_TO_DISPLAY = 10;
    const topAccounts = sortedAccountEntries.slice(0, MAX_ACCOUNTS_TO_DISPLAY);
    const remainingAccounts = sortedAccountEntries.slice(MAX_ACCOUNTS_TO_DISPLAY);
    
    const sortedAccounts = topAccounts.map(([accountNumber]) => accountNumber);
    const hasMoreAccounts = remainingAccounts.length > 0;

    // If no accounts found, use total cost only
    if (sortedAccounts.length === 0) {
      const months = monthOrder.map((monthKey) => `${monthMap[monthKey]} ${currentYear}`);
      const totalCostData = monthOrder.map((monthKey) => {
        const monthData = monthlyData[monthKey];
        return monthData?.orgMonthCost || 0;
      });
      return {
        categories: months,
        series: [{
          name: "Total Cost",
          data: totalCostData,
        }],
        accountNames: ["Total Cost"],
        totalCostData: totalCostData,
      };
    }

    // Build series data for each top account
    const series = sortedAccounts.map((accountNumber) => {
      const accountData = monthOrder.map((monthKey) => {
        const monthData = monthlyData[monthKey];
        const accountsMap = monthData?.accountsCostMonthMap;
        
        if (accountsMap && typeof accountsMap === 'object' && accountsMap[accountNumber] !== undefined) {
          // Handle string values (convert to number)
          const value = accountsMap[accountNumber];
          return typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
        }
        return 0;
      });

      return {
        name: accountNumber,
        data: accountData,
      };
    });

    // Add "Others" category if there are more accounts
    if (hasMoreAccounts) {
      const othersData = monthOrder.map((monthKey) => {
        const monthData = monthlyData[monthKey];
        const accountsMap = monthData?.accountsCostMonthMap;
        let othersTotal = 0;
        
        if (accountsMap && typeof accountsMap === 'object') {
          remainingAccounts.forEach(([accountNumber]) => {
            if (accountsMap[accountNumber] !== undefined) {
              const value = accountsMap[accountNumber];
              othersTotal += typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
            }
          });
        }
        
        return othersTotal;
      });

      series.push({
        name: `Others (${remainingAccounts.length} accounts)`,
        data: othersData,
      });
    }

    const months = monthOrder.map((monthKey) => `${monthMap[monthKey]} ${currentYear}`);

    // Also prepare total cost data for line chart
    const totalCostData = monthOrder.map((monthKey) => {
      const monthData = monthlyData[monthKey];
      return monthData?.orgMonthCost || 0;
    });

    return {
      categories: months,
      series: series,
      accountNames: sortedAccounts,
      totalCostData: totalCostData,
    };
  }, [data]);

  // Color palette for stacked bars (using a variety of colors)
  // Extended palette to support up to 11 items (10 accounts + Others)
  const colorPalette = [
    "#3b82f6", // Blue
    "#10b981", // Green
    "#f59e0b", // Amber
    "#ef4444", // Red
    "#8b5cf6", // Purple
    "#06b6d4", // Cyan
    "#f97316", // Orange
    "#84cc16", // Lime
    "#ec4899", // Pink
    "#6366f1", // Indigo
    "#94a3b8", // Slate (for Others category)
  ];

  // Bar chart options
  const barChartOptions = {
    chart: {
      type: "bar",
      height: 350,
      stacked: true,
      toolbar: {
        show: false,
      },
    },
    colors: colorPalette,
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: "60%",
        dataLabels: {
          position: "top",
          total: {
            enabled: true,
            formatter: function (val) {
              if (val === 0 || val === null || val === undefined) {
                return "";
              }
              if (val >= 1000) {
                return "$" + (val / 1000).toFixed(1) + "K";
              }
              return "$" + val.toFixed(0);
            },
            style: {
              fontSize: "12px",
              fontWeight: 600,
              color: "#64748b",
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false, // Disable individual segment labels for cleaner look
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: {
          colors: "#64748b",
          fontSize: "12px",
          fontFamily: "Inter, sans-serif",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        formatter: function (value) {
          if (value >= 1000) {
            return "$" + (value / 1000).toFixed(1) + "K";
          }
          return "$" + value.toFixed(0);
        },
        style: {
          colors: "#64748b",
          fontSize: "12px",
          fontFamily: "Inter, sans-serif",
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (value) {
          if (value > 0) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(value);
          }
          return "";
        },
      },
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "12px",
      fontFamily: "Inter, sans-serif",
      markers: {
        width: 12,
        height: 12,
        radius: 6,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
  };

  // Line chart options
  const lineChartOptions = {
    chart: {
      type: "line",
      height: 350,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    colors: colorPalette, // Use same color palette as bar chart
    stroke: {
      curve: "smooth",
      width: 2.5,
    },
    dataLabels: {
      enabled: false,
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: {
          colors: "#64748b",
          fontSize: "12px",
          fontFamily: "Inter, sans-serif",
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        formatter: function (value) {
          if (value >= 1000) {
            return "$" + (value / 1000).toFixed(1) + "K";
          }
          return "$" + value.toFixed(0);
        },
        style: {
          colors: "#64748b",
          fontSize: "12px",
          fontFamily: "Inter, sans-serif",
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (value) {
          if (value > 0) {
            return new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(value);
          }
          return "";
        },
      },
    },
    markers: {
      size: 3,
      hover: {
        size: 5,
      },
    },
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "12px",
      fontFamily: "Inter, sans-serif",
      markers: {
        width: 12,
        height: 12,
        radius: 6,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
  };

  // Use stacked series for bar chart, individual account lines for line chart
  const barSeries = chartData.series || [];
  // For line chart, show individual account trends (same data as bar chart but as separate lines)
  const lineSeries = chartData.series && chartData.series.length > 0 
    ? chartData.series 
    : (chartData.totalCostData ? [{
        name: "Total Monthly Cost",
        data: chartData.totalCostData,
      }] : []);
  
  const series = chartType === "bar" ? barSeries : lineSeries;

  const handleChartTypeChange = (event, newType) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Monthly Cost
          </Typography>
          {chartData.hasMoreAccounts && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.75rem",
                display: "block",
                mt: 0.5,
              }}
            >
              Showing top 10 accounts. {chartData.totalAccountsCount - 10} more grouped as "Others"
            </Typography>
          )}
        </Box>
        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={handleChartTypeChange}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              px: 2,
              py: 0.5,
              fontSize: "0.875rem",
              borderColor: "divider",
              "&.Mui-selected": {
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              },
            },
          }}
        >
          <ToggleButton value="bar" aria-label="bar chart">
            <BarChartIcon sx={{ fontSize: 18, mr: 0.5 }} />
            Bar
          </ToggleButton>
          <ToggleButton value="line" aria-label="line chart">
            <ShowChartIcon sx={{ fontSize: 18, mr: 0.5 }} />
            Trend
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {chartData.categories.length > 0 && series.length > 0 ? (
        <Box sx={{ height: 350, flexShrink: 0 }}>
          <ReactApexChart
            options={chartType === "bar" ? barChartOptions : lineChartOptions}
            series={series}
            type={chartType}
            height="100%"
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 350,
            flexShrink: 0,
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">No data available</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default FinOpsMonthlyCostChart;
